import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

/**
 * Reusable Confirmation Modal Component
 * Can be used for delete, restart, or other confirmation actions
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed (without confirming)
 * @param {function} onConfirm - Callback when user confirms the action
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} itemName - Name of the item (optional, for display)
 * @param {boolean} isSpanish - Language preference
 * @param {string} confirmButtonText - Text for confirm button (default: 'Delete' or 'Eliminar')
 * @param {string} confirmButtonColor - Color variant for confirm button ('red' or 'orange', default: 'red')
 * @param {string} iconColor - Color for the alert icon ('red' or 'orange', default: 'red')
 */
export const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  isSpanish = false,
  confirmButtonText,
  confirmButtonColor = 'red',
  iconColor = 'red',
}) => {
  if (!isOpen) return null;

  const defaultTitle = isSpanish 
    ? 'Confirmar eliminación' 
    : 'Confirm Delete';

  const defaultMessage = isSpanish
    ? '¿Está seguro de que desea eliminar este elemento? Esta acción no se puede deshacer.'
    : 'Are you sure you want to delete this item? This action cannot be undone.';

  // Determine button text and colors based on action type
  const defaultConfirmText = isSpanish ? 'Eliminar' : 'Delete';
  const finalConfirmText = confirmButtonText || defaultConfirmText;
  const cancelText = isSpanish ? 'Cancelar' : 'Cancel';
  
  const buttonColorClasses = {
    red: 'bg-red-600 hover:bg-red-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
  };
  
  const iconColorClasses = {
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  const buttonColor = buttonColorClasses[confirmButtonColor] || buttonColorClasses.red;
  const iconColorClass = iconColorClasses[iconColor] || iconColorClasses.red;

  const handleConfirm = () => {
    onConfirm();
    onClose(); // Close modal after confirming
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconColorClass}`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h2 className={`text-xl font-bold ${iconColor === 'orange' ? 'text-orange-600' : 'text-red-600'}`}>
              {title || defaultTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 mb-2">
            {message || defaultMessage}
          </p>
          {itemName && (
            <p className="font-semibold text-gray-900 mt-2">
              "{itemName}"
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm ${buttonColor} text-white rounded-full transition-colors`}
          >
            {finalConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;

